import { useCallback, useEffect, useMemo, useState } from 'react';

import type { getAppNavigationContainerRef } from '../../navigation/AppNavigation.ref';
import { featureFlagOverrides } from './overrides';
import type { FeatureFlagOverrides } from './types';

/**
 * Returns the feature flag overrides with the correct priority.
 *
 * 1. Query overrides (?features.<feature-name>=<feature-value>).
 * 2. Local overrides (apps/order/src/context/LaunchDarkly/overrides.json).
 */
export const useOverrides = () => {
  const { queryOverrides, loadQueryOverrides } = useQueryOverrides();
  const { localOverrides } = useLocalOverrides();

  return useMemo(() => {
    const overrides = {
      ...localOverrides,
      ...queryOverrides,
    };

    return { overrides, loadOverrides: loadQueryOverrides };
  }, [queryOverrides, localOverrides, loadQueryOverrides]);
};

const useLocalOverrides = () => {
  const [localOverrides, setLocalOverrides] = useState(featureFlagOverrides);

  return { localOverrides, setLocalOverrides };
};

const useQueryOverrides = () => {
  const { params, loadParams } = useParamsFromReference();
  const [queryOverrides, setQueryOverrides] = useState<FeatureFlagOverrides>(
    {},
  );

  // Updates with existing to avoid clearing on page change.
  useEffect(() => {
    setQueryOverrides((existingOverrides) => ({
      ...existingOverrides,
      ...params,
    }));
  }, [params, setQueryOverrides]);

  return { queryOverrides, setQueryOverrides, loadQueryOverrides: loadParams };
};

const useParamsFromReference = () => {
  const [params, setParams] = useState({});

  const loadParams = useCallback(
    (
      navigationContainerRef: ReturnType<typeof getAppNavigationContainerRef>,
    ) => {
      const navigation = navigationContainerRef?.current;
      const route = navigation?.getCurrentRoute?.();
      const allParams = route?.params ?? {};
      const { routeParams, featureParams } = parseParametersFromQuery(
        allParams as Record<string, string>,
      );

      // Removes feature params from navigation (setting as undefined).
      navigation?.setParams?.({ ...allParams, ...routeParams });

      // Loads feature parameters into state.
      setParams(featureParams);
    },
    [],
  );

  return { params, loadParams };
};

// Receives all parameters from navigation,
// Returns only the route params and only the feature params.
const parseParametersFromQuery = (params: Record<string, string>) => {
  // Selects all parameters that starts with "features.".
  const featureFlagsFromParams = Object.keys(params).filter((key) =>
    key.startsWith('features.'),
  );

  // Maps query params into feature flags.
  // From: { features.<feature-name>: '<feature-value>' }
  // To: { feature-name: feature-value }
  const featureParams = Object.fromEntries(
    featureFlagsFromParams.map((key) => [
      key.split('features.')[1],
      mapParamToFeatureValue(params[key as never]),
    ]),
  );

  // Sets all params that contain "features." in routeParams to undefined.
  const routeParams = Object.fromEntries(
    featureFlagsFromParams.map((key) => [key, undefined]),
  );

  return {
    featureParams,
    routeParams,
  };
};

// The parameters are received as "true", "false", "something".
// This converts "true" to true and "false" to false, while keeping "something".
const mapParamToFeatureValue = (param: string): boolean | string => {
  if (param === 'true') return true;

  if (param === 'false') return false;

  return param;
};
